import { readEnvVariable } from '.';
import { getFeatureFlagValue } from './network';
import logger from './logger';

export interface PrefetchResult {
  flag: boolean;
  durationMs: number;
}

let prefetchPromise: Promise<PrefetchResult> | null = null;
let prefetchedForRestaurant: string | null = null;

/**
 * Pre-fetches the unified_menu feature flag for a restaurant code.
 * Called when a task assignment WS message arrives so the flag is
 * already resolved by the time RestaurantsContainer mounts.
 * The returned promise resolves with both the flag value and the fetch duration.
 *
 * NOTE: Does NOT set session storage — RestaurantsContainer handles that
 * after updateContext completes to ensure the Unleash SDK context is correct.
 */
export const prefetchMenuUnificationFlag = (
  restaurantCode: string
): Promise<PrefetchResult> => {
  prefetchedForRestaurant = restaurantCode;
  const startTime = performance.now();
  prefetchPromise = getFeatureFlagValue({
    url: `${readEnvVariable('UNLEASH_API_URL')}?userId=${restaurantCode}`,
    name: 'unified_menu',
  })
    .then((flag) => {
      const durationMs = performance.now() - startTime;
      logger.info({
        message: `Prefetched unified_menu feature flag for ${restaurantCode}: ${flag}`,
        durationMs,
      });
      return { flag, durationMs };
    })
    .catch((error) => {
      const durationMs = performance.now() - startTime;
      logger.error({
        message: `Failed to prefetch unified_menu feature flag for ${restaurantCode}`,
        error,
        durationMs,
      });
      prefetchPromise = null;
      prefetchedForRestaurant = null;
      return { flag: false, durationMs };
    });
  return prefetchPromise;
};

/**
 * Consumes the prefetched flag promise if it matches the given restaurant.
 * Returns the promise (which resolves with flag + durationMs), or null if
 * no prefetch was done.
 */
export const consumePrefetchedFlag = (
  restaurantCode: string
): Promise<PrefetchResult> | null => {
  if (prefetchPromise && prefetchedForRestaurant === restaurantCode) {
    const promise = prefetchPromise;
    prefetchPromise = null;
    prefetchedForRestaurant = null;
    return promise;
  }
  return null;
};
